import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2Icon } from "lucide-react"
import { Slot } from "radix-ui"

import { cn } from "../../lib/utils"

function wrapButtonContent(children: React.ReactNode): React.ReactNode {
  const items = React.Children.toArray(children).filter((c) => c != null)
  if (items.length === 0) return null

  return (
    <span className="inline-flex min-w-0 max-w-full items-center justify-start gap-1.5">
      {items.map((child, index) =>
        typeof child === "string" || typeof child === "number" ? (
          <span key={`btn-txt-${index.toString()}`} className="min-w-0 truncate">
            {child}
          </span>
        ) : (
          child
        ),
      )}
    </span>
  )
}

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 inline-flex max-w-full min-w-0 shrink items-center justify-center gap-1.5 overflow-hidden rounded-lg border border-transparent bg-clip-padding text-sm font-semibold outline-none transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-200 focus-visible:ring-3 aria-invalid:ring-3 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 group/button select-none whitespace-nowrap",
  {
    variants: {
      variant: {
        default:
          "border-primary/70 bg-primary bg-[linear-gradient(to_bottom,rgba(255,255,255,0.14)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.06)_100%)] bg-blend-overlay text-primary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_-1px_0_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.22),0_10px_18px_-18px_hsl(var(--primary)/0.85)] hover:brightness-[1.05]",
        text: "bg-transparent text-foreground shadow-none hover:bg-transparent dark:hover:bg-transparent",
        outline:
          "border-border/80 bg-background bg-[linear-gradient(to_bottom,rgba(255,255,255,0.09)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.04)_100%)] bg-blend-overlay text-foreground shadow-[var(--shadow-field)] hover:bg-muted/70 hover:text-foreground dark:bg-input dark:border-border dark:hover:bg-input/85 aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "border-border/80 bg-background bg-[linear-gradient(to_bottom,rgba(255,255,255,0.09)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.04)_100%)] bg-blend-overlay text-foreground shadow-[var(--shadow-field)] hover:bg-muted/70 hover:text-foreground dark:bg-input dark:border-border dark:hover:bg-input/85 aria-expanded:bg-muted aria-expanded:text-foreground",
        ghost:
          "border-transparent text-foreground/80 shadow-none hover:border-border/60 hover:bg-muted/70 hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
        // Solid danger — the default for destructive actions. Use `destructiveSubtle` for the soft tint.
        destructive:
          "border-destructive/70 bg-destructive bg-[linear-gradient(to_bottom,rgba(255,255,255,0.14)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.06)_100%)] bg-blend-overlay text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_-1px_0_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.22),0_10px_18px_-18px_hsl(var(--destructive)/0.85)] hover:brightness-[1.05] focus-visible:ring-destructive/30 focus-visible:border-destructive dark:text-white",
        // Soft-tinted danger — opt in where a full solid red would be too heavy.
        destructiveSubtle:
          "border-destructive/35 bg-destructive/12 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.09)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.04)_100%)] bg-blend-overlay text-destructive shadow-[inset_0_1px_0_rgba(255,255,255,0.1),inset_0_-1px_0_rgba(127,29,29,0.12),0_2px_8px_rgba(0,0,0,0.18),0_10px_18px_-20px_hsl(var(--destructive)/0.55)] hover:bg-destructive/18 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
        // Tonal affirmative (e.g. approve / mark ready). Mirrors the soft-tint destructive style.
        success:
          "border-emerald-600/30 bg-emerald-600/12 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.09)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.04)_100%)] bg-blend-overlay text-emerald-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_2px_8px_rgba(0,0,0,0.12)] hover:bg-emerald-600/18 focus-visible:ring-emerald-600/25 focus-visible:border-emerald-600/40 dark:text-emerald-400 dark:bg-emerald-500/15 dark:hover:bg-emerald-500/25",
        // Tonal caution (e.g. request changes / revise a running budget).
        warning:
          "border-amber-500/40 bg-amber-500/15 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.09)_0%,transparent_42%,transparent_58%,rgba(255,255,255,0.04)_100%)] bg-blend-overlay text-amber-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_2px_8px_rgba(0,0,0,0.12)] hover:bg-amber-500/22 focus-visible:ring-amber-500/30 focus-visible:border-amber-500/50 dark:text-amber-400 dark:bg-amber-500/15 dark:hover:bg-amber-500/25",
        link: "!h-auto !rounded-none !border-0 !bg-transparent !px-0 !py-0 text-primary shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-control gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-control-xs gap-1 rounded-[calc(var(--radius)-1px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-control-sm gap-1.5 rounded-[calc(var(--radius)+1px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-control-lg gap-2 px-4 text-[0.925rem] has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5",
        icon: "size-control",
        "icon-xs":
          "size-control-xs rounded-[calc(var(--radius)-1px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-control-sm rounded-[calc(var(--radius)+1px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-control-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    isLoading?: boolean
    loadingIndicator?: React.ReactNode
  }

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  isLoading = false,
  loadingIndicator,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  const loadingState = loading || isLoading
  const indicator = loadingState ? loadingIndicator ?? <Loader2Icon className="size-4 animate-spin" /> : null

  if (asChild) {
    const child = React.Children.only(children) as React.ReactElement<{ children?: React.ReactNode }>
    const childWithIndicator = loadingState
      ? React.cloneElement(child, undefined, <>
          {indicator}
          {child.props.children}
        </>)
      : child

    return (
      <Comp
        data-slot="button"
        data-control=""
        data-variant={variant}
        data-size={size}
        data-loading={loadingState ? "true" : undefined}
        aria-busy={loadingState || undefined}
        aria-disabled={disabled || loadingState || undefined}
        className={cn(
          buttonVariants({ variant, size, className }),
          loadingState && "data-[loading=true]:pointer-events-none",
        )}
        {...props}
      >
        {childWithIndicator}
      </Comp>
    )
  }

  return (
    <Comp
      data-slot="button"
        data-control=""
      data-variant={variant}
      data-size={size}
      data-loading={loadingState ? "true" : undefined}
      aria-busy={loadingState || undefined}
      disabled={disabled || loadingState}
      className={cn(
        buttonVariants({ variant, size, className }),
        loadingState && "data-[loading=true]:pointer-events-none",
      )}
      {...props}
    >
      {indicator}
      {wrapButtonContent(children)}
    </Comp>
  )
}

export { Button, buttonVariants }
